import type { Quiz, QuizAdditionalCategory } from '@efdigital/ll-addon-data';
import { DataTypeQuiz } from '@efdigital/ll-addon-data/data-types';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { Type } from '@warp-drive/core/types/symbols';

import { CategoryScore } from './category-score.gts';

interface Signature {
  Args: {
    /**
     * The quiz that the question belongs to.
     */
    quiz: Quiz;

    /**
     * The category cid that the score belongs to. Use null or empty string for grand total only.
     */
    category: string | null | undefined;

    /**
     * The score for the category.
     */
    score: number | null | undefined;

    /**
     * The additional categories for the score.
     */
    additionalCategories: QuizAdditionalCategory[];

    onChange: (
      score: number,
      category: string,
      additionalCategories: QuizAdditionalCategory[]
    ) => void;
  };
}

export class CategoryScores extends Component<Signature> {
  setScore = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.onChange(
      parseInt(target.value) || 0,
      this.args.category || '',
      this.args.additionalCategories
    );
  };

  setCategory = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    this.args.onChange(
      this.currentScore,
      target.value,
      this.args.additionalCategories
    );
  };

  addCategory = () => {
    const newAdditionalCategory: QuizAdditionalCategory = {
      [Type]: DataTypeQuiz.QuizAdditionalCategory,
      score: 0,
      category: '',
    };

    this.args.onChange(this.currentScore, '', [
      ...this.args.additionalCategories,
      newAdditionalCategory,
    ]);
  };

  get currentScore(): number {
    return this.args.score || 0;
  }

  get selectedCategory(): string {
    return this.args.category || '';
  }

  get allScoresAndCategories(): {
    score: number;
    category: string;
    additionalCategoryIndex: number;
    isRoot: boolean;
    remove: () => void;
    update: (score: number, category: string) => void;
  }[] {
    return [
      {
        score: this.currentScore,
        category: this.selectedCategory,
        additionalCategoryIndex: -1,
        isRoot: true,
        update: (score: number, category: string) => {
          this.args.onChange(score, category, this.args.additionalCategories);
        },
        remove: () => {
          if (this.args.additionalCategories.length > 0) {
            const firstAdditionalCategory = this.args.additionalCategories[0]!;
            this.args.onChange(
              firstAdditionalCategory.score || 0,
              firstAdditionalCategory.category || '',
              this.args.additionalCategories.slice(1)
            );
          }
        },
      },
      ...this.args.additionalCategories.map((category, index) => ({
        score: category.score || 0,
        category: category.category || '',
        additionalCategoryIndex: index,
        isRoot: false,
        update: (score: number, category: string) => {
          this.args.additionalCategories[index]!.score = score;
          this.args.additionalCategories[index]!.category = category;
          this.args.onChange(
            this.currentScore,
            this.selectedCategory,
            this.args.additionalCategories
          );
        },
        remove: () => {
          const newAdditionalCategories =
            this.args.additionalCategories.slice();
          newAdditionalCategories.splice(index, 1);
          this.args.onChange(
            this.currentScore,
            this.selectedCategory,
            newAdditionalCategories
          );
        },
      })),
    ];
  }

  get hasOnlyOneCategory(): boolean {
    return this.args.additionalCategories.length < 1;
  }

  <template>
    <div class="position-relative" ...attributes>
      {{#each this.allScoresAndCategories as |scoreAndCategory|}}
        <div class="d-flex align-items-end gap-2">
          <CategoryScore
            @quiz={{@quiz}}
            @category={{scoreAndCategory.category}}
            @score={{scoreAndCategory.score}}
            @onChange={{scoreAndCategory.update}}
            @labelClass={{unless scoreAndCategory.isRoot "visually-hidden"}}
            class="flex-grow-1"
          />
          <button
            type="button"
            class="btn btn-dark btn-sm px-2 py-1 my-1 rounded-2"
            {{on "click" scoreAndCategory.remove}}
            disabled={{this.hasOnlyOneCategory}}
          ><span class="visually-hidden">Remove</span><iconify-icon
              icon="ph:minus-bold"
              inline
            /></button>
        </div>
      {{/each}}
      <button
        type="button"
        class="btn btn-dark btn-sm px-2 py-1 rounded-2 position-absolute top-0 end-0"
        {{on "click" this.addCategory}}
      ><span class="visually-hidden">Add</span><iconify-icon
          icon="ph:plus-bold"
          inline
        /></button>
    </div>
  </template>
}
